import { ChangeDetectorRef, Component, inject, signal } from '@angular/core';
import {
  type AbstractControl,
  type FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { debounceTime, distinctUntilChanged, merge, take } from 'rxjs';

import { emailValidator } from '@core/common-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  TellentManageRedirectService,
  TellentMembersDataFacade,
  UserInvitationFacade,
} from '@core/tellent-logic';
import { AddEmployeeForPreonboardingPayload } from '@core/tellent-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  FormErrorDirective,
  FormFieldComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  ProtectedControlDirective,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

type FormField<T> = {
  personalEmail: FormControl<string | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
} & T;

type AddEmployeeForm = {
  employees: FormArray<FormGroup<FormField<{}>>>;
};

const removeDuplicateError = (control: AbstractControl<null | string, null | string> | null) => {
  if (control?.errors?.duplicatedEmail) {
    const { duplicatedEmail, ...errors } = control?.errors;
    control?.setErrors(errors && Object.keys(errors).length ? { ...errors } : null);
  }
};

@Component({
  selector: 'tla-add-employee-for-preonboarding-dialog',
  templateUrl: './add-employee-for-preonboarding-dialog.component.html',
  styleUrls: ['./add-employee-for-preonboarding-dialog.component.less'],
  imports: [
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    ButtonComponent,
    TranslatePipe,
    FormFieldComponent,
    FormsModule,
    InputComponent,
    LabelComponent,
    ProtectedControlDirective,
    ReactiveFormsModule,
    FormErrorDirective,
    IconButtonComponent,
    IconComponent,
    ButtonLoadingDirective,
  ],
  providers: [TellentManageRedirectService],
})
export class AddEmployeeForPreonboardingDialogComponent extends DialogComponent<{}> {
  private readonly fb = inject(FormBuilder);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly userInvitationFacade = inject(UserInvitationFacade);
  private readonly untilDestroyed = injectUntilDestroyed();
  private readonly t = injectTranslate();
  private readonly redirectService = inject(TellentManageRedirectService);

  public canAddRow = signal(true);
  public loading = signal(false);

  public formGroup: FormGroup<AddEmployeeForm>;

  public emailFieldErrors = {
    required: this.t('tlc.common.errors.required'),
    email: this.t('tlc.common.errors.email'),
    duplicatedEmail: this.t('tlc.common.errors.duplicated_email'),
  };

  public ngOnInit(): void {
    this.formGroup = this.fb.group({
      employees: this.fb.array([this.createEmployeeGroup()]),
    });

    this.formGroup.controls.employees.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(300), this.untilDestroyed)
      .subscribe(employees => {
        const controls = this.formGroup.controls.employees.controls;
        const emails = controls.map(control => control.get('personalEmail')?.value);

        if (employees.length === 1) {
          const control = this.formGroup.controls.employees.controls[0].get('personalEmail');
          if (control) removeDuplicateError(control);
        } else {
          controls.forEach(control => {
            const email = control.get('personalEmail');

            if (
              emails.some(
                (emailValue, i) => emails.indexOf(emailValue) !== i && emailValue === email?.value,
              )
            ) {
              email?.setErrors({ duplicatedEmail: true });
            } else {
              removeDuplicateError(email);
            }
          });
        }
      });
  }

  public get employees() {
    return this.formGroup.controls.employees.controls;
  }

  public hasAnyEmptyEmail(): boolean {
    return this.formGroup.controls.employees.controls.some(
      control => !control.get('personalEmail')?.value,
    );
  }

  public hasAnyEmptyFirstName(): boolean {
    return this.formGroup.controls.employees.controls.some(
      control => !control.get('firstName')?.value,
    );
  }

  public hasAnyEmptyLastName(): boolean {
    return this.formGroup.controls.employees.controls.some(
      control => !control.get('lastName')?.value,
    );
  }

  public continue(): void {
    const employees = <Partial<AddEmployeeForPreonboardingPayload>>(
      this.formGroup.controls.employees.value
    );

    this.loading.set(true);
    this.userInvitationFacade.addEmployeeForPreonboarding(employees);

    this.handleSubmitSuccess();
    this.handleSubmitFailure();
  }

  public cancel(): void {
    this.dialogRef.close(undefined);
  }

  public addMemberFormGroup() {
    this.formGroup.controls.employees.push(this.createEmployeeGroup());
    this.canAddRow.set(this.formGroup.controls.employees.length < 50);
    this.cdRef.detectChanges();
  }

  public removeMemberFormGroup(index: number) {
    this.formGroup.controls.employees.removeAt(index);
    this.canAddRow.set(this.formGroup.controls.employees.length < 50);
    this.cdRef.detectChanges();
  }

  private createEmployeeGroup() {
    return this.fb.group({
      personalEmail: ['', [Validators.required, emailValidator]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
    });
  }

  private handleSubmitSuccess(): void {
    merge(
      this.membersDataFacade.inviteTellentMemberSuccess$,
      this.membersDataFacade.inviteAppMemberSuccess$,
    )
      .pipe(take(1), this.untilDestroyed)
      .subscribe(({ payload }) => {
        const memberGuids = payload.map(member => member.guid);
        this.redirectService.sendMembersForPreonboarding(memberGuids);
        this.dialogRef.close({ success: true, payload });
      });
  }

  private handleSubmitFailure(): void {
    merge(
      this.membersDataFacade.inviteTellentMemberFail$,
      this.membersDataFacade.inviteAppMemberFail$,
    )
      .pipe(take(1), this.untilDestroyed)
      .subscribe(() => this.loading.set(false));
  }
}
